/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link, Loading } from '@carbon/react';
import Copier from './Copier';
import jsyaml from 'js-yaml';
import classNames from 'classnames';
import { cleanUpKey, isExpandable } from '../utils/common';
import { CodeViewer } from './CodeViewer';

const ExtensionDisplay = ({ extensionBody, extensionName }) => {
  const [contentState, updateContentState] = useState(null);
  const [expandedState, updateExpandedState] = useState(false);

  const { t } = useTranslation();

  const _isMounted = useRef(false);
  const copierRef=useRef(null);

  useEffect(() => {
    _isMounted.current = true;
    return () => {
      _isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (extensionBody) {
      // Continue the hack from ExtensionsDisplay.js - if the string 'false'
      // is passed in, send false instead
      if (extensionBody === 'false') {
        callHighlighter(false);
      } else {
        callHighlighter(extensionBody);
      }
    }
  }, [extensionBody]);

  const callHighlighter = async (extension) => {
    try {
      let data = jsyaml.dump(extension, null, 2);
      if (_isMounted.current && data) {
        updateContentState(data);
      }
    } catch (e) {
      console.error(e);
      updateContentState(null);
    }
  };

  const expand = () => {
    updateExpandedState(!expandedState);
  };

  if (extensionName && extensionName.startsWith('x-')) {
    extensionName = extensionName.substr(2);
  }

  let largeContent = isExpandable(contentState, 8);

  return (
    <div className={classNames('extensionDisplay', `extension-${cleanUpKey(extensionName)}`)}>
      <div className="extensionName">{extensionName}</div>

      {!contentState && <Loading small withOverlay={false} data-testid={'extension-loading'} />}
      {contentState && (
        <div className={classNames('extensionBody', 'cds--snippet', 'cds--snippet--multi', 'cds--snippet--expand')}>
          <div ref={copierRef} className={classNames(`extension_${cleanUpKey(extensionName)}`, { expanded: expandedState }, { small: !largeContent })}>
            <CodeViewer codeLanguage={'yaml'}>{contentState}</CodeViewer>
          </div>
          <Copier element={`.extension_${cleanUpKey(extensionName)}`} copierRef={copierRef}/>
          <Link className={classNames('expandCollapseLink', { hidden: !largeContent })} onClick={expand} data-testid={'expand-link'}>
            {expandedState ? t('show_less') : t('show_more')}
          </Link>
        </div>
      )}
    </div>
  );
};

ExtensionDisplay.propTypes = {
  extensionBody: PropTypes.any, // The value of the Info Extension object
  extensionName: PropTypes.string // The type of extension object
};

export default ExtensionDisplay;
